<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, markRaw, onMounted, ref, watch } from 'vue';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import UploadLegalDocumentComponent from '@/components/legalDocument/UploadLegalDocumentComponent.vue';
import LegalDocumentService from '@/services/legalDocument/legalDocumentService.ts';
import { useAuthStore } from '@/stores/authStore.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { formatFullDate, formatItemValue } from '@/utils/formatters.ts';
import { LegalDocumentTypeEnum } from '@/enums/legalDocument_enum.ts';

import type { DataTableHeader } from 'vuetify/framework';
import type { LegalDocument, CreateLegalDocumentRequest } from '@/types/services/legalDocument/legalDocument.type.ts';

const { t } = useI18n()
const authStore = useAuthStore()

const documents = ref<LegalDocument[]>([])
const loading = ref<boolean>(false)

const typeFilter = ref<LegalDocumentTypeEnum | null>(null)
const limit = ref<number>(10)
const page = ref<number>(1)
const total = ref<number>(0)
const maxPages = ref<number>(1)

const typeItems = computed(() => [
  { title: t('views.legalDocumentList.types.all'), value: null },
  { title: t('views.legalDocumentList.types.terms_of_service'), value: LegalDocumentTypeEnum.TERMS_OF_SERVICE },
  { title: t('views.legalDocumentList.types.privacy_policy'), value: LegalDocumentTypeEnum.PRIVACY_POLICY },
])

const headers = computed<DataTableHeader[]>(() => [
  { title: t('views.legalDocumentList.header.type'), key: 'documentType', sortable: false, align: 'start' },
  { title: t('views.legalDocumentList.header.version'), key: 'version', sortable: false, align: 'start' },
  { title: t('views.legalDocumentList.header.name'), key: 'name', sortable: false, align: 'start' },
  { title: t('views.legalDocumentList.header.changeNote'), key: 'changeNote', sortable: false, align: 'start' },
  { title: t('views.legalDocumentList.header.author'), key: 'author', sortable: false, align: 'start' },
  { title: t('views.legalDocumentList.header.createdAt'), key: 'createdAt', sortable: false, align: 'start' },
  { title: t('views.legalDocumentList.header.actions'), key: 'actions', sortable: false, width: '100px', align: 'end' },
])

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission)
}

async function fetchDocuments(): Promise<void> {
  try {
    loading.value = true
    const response = await LegalDocumentService.getPaginated(page.value, limit.value, typeFilter.value)
    documents.value = response.data
    total.value = response.total
    maxPages.value = response.maxPages
  } finally {
    loading.value = false
  }
}

onMounted(fetchDocuments)

watch(typeFilter, () => {
  // Changing the filter restarts from page 1 (the page watcher below refetches if it moves).
  if (page.value !== 1) {
    page.value = 1
  } else {
    fetchDocuments()
  }
})
watch([limit, page], fetchDocuments)

async function addVersion(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(UploadLegalDocumentComponent),
  })

  if (modal && typeof modal === 'object') {
    await LegalDocumentService.create(modal as CreateLegalDocumentRequest)
    displayToast(t('views.legalDocumentList.createSuccess'), 'success')
    if (page.value !== 1) {
      page.value = 1
    } else {
      await fetchDocuments()
    }
  }
}

async function downloadDocument(document: LegalDocument): Promise<void> {
  const url = await LegalDocumentService.getDownloadUrl(document.id)
  if (url) {
    window.open(url, '_blank')
  }
}
</script>

<template>
  <MainTitleComponent icon="PhScales">
    {{ t('views.legalDocumentList.title') }}
  </MainTitleComponent>

  <v-row justify="space-between" align="center">
    <v-col cols="12" sm="5">
      <v-select
        v-model="typeFilter"
        :items="typeItems"
        :label="t('views.legalDocumentList.filter')"
        density="compact"
        hide-details
      />
    </v-col>

    <v-col v-if="hasPermission(['ADD_LEGAL_DOCUMENT'])" cols="12" sm="auto">
      <v-btn class="w-100 w-sm-auto" @click.prevent="addVersion">
        {{ t('views.legalDocumentList.buttons.create') }}
        <template #prepend>
          <v-icon icon="PhUploadSimple" />
        </template>
      </v-btn>
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="documents"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        hover
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.documentType`]="{ item }">
          {{ t('views.legalDocumentList.types.' + item.documentType) }}
        </template>
        <template #[`item.version`]="{ item }">
          <div class="d-flex align-center ga-2">
            <span>v{{ item.version }}</span>
            <v-chip v-if="item.isCurrent" color="success" size="small">
              {{ t('views.legalDocumentList.current') }}
            </v-chip>
          </div>
        </template>
        <template #[`item.changeNote`]="{ item }">
          {{ formatItemValue(item.changeNote) }}
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatFullDate(item.createdAt) }}
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn variant="plain" @click.stop.prevent="downloadDocument(item)">
            <v-icon icon="PhDownload" />
          </v-btn>
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>
